/**
 * StepEditor - Modal para crear/editar pasos de campaña
 */

import { useState, useEffect } from 'react'

export default function StepEditor({ step, maxStepNumber, onSave, onClose }) {
  const [formData, setFormData] = useState({
    step_number: maxStepNumber,
    days_after_enrollment: 0,
    send_hour: 10,
    send_minute: 0,
    message_text: '',
    image_url: '',
    wassenger_file_id: '',
    is_active: true
  })
  const [saving, setSaving] = useState(false)
  const [collisionDialog, setCollisionDialog] = useState(null)
  // collisionDialog shape: { type: 'step_collision' | 'cohort_active', meta: {...}, formData: {...} }

  useEffect(() => {
    if (step) {
      setFormData({
        step_number: step.step_number || maxStepNumber,
        days_after_enrollment: step.days_after_enrollment || 0,
        send_hour: step.send_hour || 10,
        send_minute: step.send_minute || 0,
        message_text: step.message_text || '',
        image_url: step.image_url || '',
        wassenger_file_id: step.wassenger_file_id || '',
        is_active: step.is_active !== false
      })
    }
  }, [step, maxStepNumber])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.message_text.trim() && !formData.image_url.trim() && !formData.wassenger_file_id.trim()) {
      alert('Debe incluir un mensaje o una imagen')
      return
    }

    setSaving(true)
    try {
      await onSave(formData)
    } catch (err) {
      const status = err.response?.status
      const body = err.response?.data
      if (status === 409 && (body?.error === 'step_collision' || body?.error === 'cohort_active')) {
        setCollisionDialog({ type: body.error, meta: body, formData })
        return
      }
      console.error('Error guardando step:', err)
      alert(err.response?.data?.error || 'Error al guardar el paso')
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmShift = async () => {
    if (!collisionDialog) return
    const formDataToSave = collisionDialog.formData
    setSaving(true)
    setCollisionDialog(null)
    try {
      await onSave(formDataToSave, { insertMode: 'shift' })
    } catch (err) {
      const status = err.response?.status
      const body = err.response?.data
      if (status === 409 && body?.error === 'cohort_active') {
        setCollisionDialog({ type: 'cohort_active', meta: body, formData: formDataToSave })
        return
      }
      console.error('Error guardando step (shift):', err)
      alert(err.response?.data?.error || 'Error al guardar el paso')
    } finally {
      setSaving(false)
    }
  }

  // Previsualizar días en formato legible
  const getDaysLabel = (days) => {
    if (days === 0) return 'Mismo día de inscripción'
    if (days === 1) return '1 día después'
    return `${days} días después`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden mx-4">
        <form onSubmit={handleSubmit}>
          {/* Header con gradiente - Estilo Koomo */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
            <h3 className="text-lg font-semibold text-white">
              {step ? 'Editar Paso' : 'Nuevo Paso'}
            </h3>
            <p className="text-primary-100 text-sm">
              {step ? 'Modifica la configuracion del paso' : 'Configura un nuevo mensaje en la secuencia'}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
            {/* Número de paso y días */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numero de paso
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.step_number}
                  onChange={(e) => handleChange('step_number', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dias despues de inscripcion
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.days_after_enrollment}
                  onChange={(e) => handleChange('days_after_enrollment', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  {getDaysLabel(formData.days_after_enrollment)}
                </p>
              </div>
            </div>

            {/* Hora de envío - intervalos de media hora */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hora de envio
              </label>
              <select
                value={`${formData.send_hour}:${formData.send_minute}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(n => parseInt(n))
                  setFormData(prev => ({ ...prev, send_hour: h, send_minute: m }))
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white transition"
              >
                {Array.from({ length: 48 }, (_, idx) => {
                  const h = Math.floor(idx / 2)
                  const m = (idx % 2) * 30
                  return (
                    <option key={idx} value={`${h}:${m}`}>
                      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')} hrs
                    </option>
                  )
                })}
              </select>
            </div>

            {/* Mensaje */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensaje
              </label>
              <textarea
                value={formData.message_text}
                onChange={(e) => handleChange('message_text', e.target.value)}
                rows={5}
                placeholder="Escribe el mensaje que se enviara..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none transition"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                {formData.message_text.length} caracteres
              </p>
            </div>

            {/* Wassenger File ID - Prioritario */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID de imagen Wassenger (recomendado)
              </label>
              <input
                type="text"
                value={formData.wassenger_file_id}
                onChange={(e) => handleChange('wassenger_file_id', e.target.value)}
                placeholder="ej: 67a1b2c3d4e5f6789012abcd"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm transition"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Obtener desde Wassenger: Biblioteca multimedia &gt; Click en imagen &gt; Copiar ID
              </p>
            </div>

            {/* URL de imagen - Alternativa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de imagen (alternativa)
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition disabled:bg-gray-50 disabled:text-gray-400"
                disabled={!!formData.wassenger_file_id}
              />
              {formData.wassenger_file_id && (
                <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                  <span>⚠</span> Se usara el ID de Wassenger en lugar de la URL
                </p>
              )}
              {formData.image_url && !formData.wassenger_file_id && (
                <div className="mt-3">
                  <img
                    src={formData.image_url}
                    alt="Vista previa"
                    className="max-h-32 rounded-xl border border-gray-200 shadow-sm"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Estado activo */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div>
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Paso activo
                </label>
                <p className="text-xs text-gray-500">
                  Los pasos inactivos se omiten en la secuencia
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl transition font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition disabled:opacity-50 font-medium shadow-md"
            >
              {saving ? 'Guardando...' : (step ? 'Guardar cambios' : 'Crear paso')}
            </button>
          </div>
        </form>
      </div>
      {collisionDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            {collisionDialog.type === 'step_collision' && (
              <>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  El paso {collisionDialog.meta.attempted_step_number} ya está ocupado
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Si lo insertas aquí, los pasos {collisionDialog.meta.attempted_step_number}-{collisionDialog.meta.current_total_steps} se desplazarán a {collisionDialog.meta.attempted_step_number + 1}-{collisionDialog.meta.current_total_steps + 1} automáticamente.
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300 p-2 rounded mb-4">
                  ⚠ La operación se bloqueará si hay una cohorte ejecutándose actualmente.
                </p>
                <div className="flex justify-end gap-2 flex-wrap">
                  <button type="button" onClick={() => setCollisionDialog(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    Cancelar
                  </button>
                  <button type="button" onClick={() => setCollisionDialog(null)} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg">
                    Cambiar número
                  </button>
                  <button type="button" onClick={handleConfirmShift} disabled={saving} className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg disabled:opacity-50">
                    {saving ? 'Guardando...' : 'Insertar y desplazar'}
                  </button>
                </div>
              </>
            )}
            {collisionDialog.type === 'cohort_active' && (
              <>
                <h3 className="text-lg font-semibold mb-3 text-red-700 dark:text-red-400">
                  ⛔ No se puede insertar paso intermedio
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  La cohorte iniciada el {collisionDialog.meta.cohort_start_date} está en ejecución (status: <span className="font-mono">{collisionDialog.meta.cohort_status}</span>).
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                  Cambiar la estructura de pasos durante una cohorte activa rompería los envíos a los contactos ya inscritos.
                </p>
                <div className="text-xs text-gray-500 mb-4 space-y-1">
                  <p>Opciones:</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>Espera a que termine la cohorte</li>
                    <li>Crea una campaña madre nueva con la estructura deseada</li>
                  </ul>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setCollisionDialog(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    Cancelar
                  </button>
                  <button type="button" onClick={() => setCollisionDialog(null)} className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg">
                    Cambiar número
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
